import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { nanoid } from 'nanoid';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { Tabs } from '#components/tabs/tabs.js';
import { UnderlineTab } from '#components/tabs/underline-tab.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useAllWizardUsers } from '../use-all-wizard-users.js';
import { InviteWizardUserForm } from './invite-wizard-user-form.js';
import { ReviewerTiersTab } from './reviewer-tiers/reviewer-tiers-tab.js';
import { WizardAdminUsersTab } from './wizard-admin-users-tab.js';
import { WizardUserTable } from './wizard-user-table/wizard-user-table.js';

import type { TabDef } from '#components/tabs/tabs.js';

const searchSchema = z.object({
	tab: z.nativeEnum(WizardAdminUsersTab).catch(WizardAdminUsersTab.Access).default(WizardAdminUsersTab.Access),
});

export const Route = createFileRoute('/_authorization-gate/wizard/admin/users/')({
	validateSearch: searchSchema,
	search: { middlewares: [stripSearchParams({ tab: WizardAdminUsersTab.Access })] },
	head: () => ({ meta: [{ title: 'Users • Wizard Admin • Cadstrom' }] }),
	component: WizardAdminUsersPage,
});

const tabDefs = [
	{ id: WizardAdminUsersTab.Access, label: 'Access' },
	{ id: WizardAdminUsersTab.ReviewerTiers, label: 'Reviewer tiers' },
] as const satisfies TabDef[];

/**
 * Wizard users, in two tabs: Access, whose capability and removal edits are drafts saved together, and Reviewer
 * tiers, whose changes save straight away. The Access draft belongs to the page rather than its tab, so it outlives a
 * switch to the other tab; switching is a navigation, which saves the draft first and stays put if that save fails.
 */
function WizardAdminUsersPage() {
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const { tab: selectedTab } = Route.useSearch();
	const navigate = Route.useNavigate();

	const handleSelectedTabChange = (tab: WizardAdminUsersTab) => {
		void navigate({ search: { tab }, replace: true });
	};

	const [showInviteForm, setShowInviteForm] = useState<{ anchorEl: HTMLElement; nonce: string } | false>(false);

	const {
		data: { users },
	} = useAllWizardUsers();

	/**
	 * Failsafe to ensure that the logic for determining which users to delete is correct.
	 *
	 * Whenever the button to remove a user is clicked, the user ID is added to this set. If, when we go to save to the
	 * server, the logic determines that a user should be deleted, but the user ID is not in this set, we know something
	 * is wrong and we can throw an error.
	 */
	const deletedUsersSanityCheck = useRef(new Set<string>());
	const { mutateAsync: deleteManyUsers, isPending: deleteManyUsersIsPending } = useMutation(
		// eslint-disable-next-line react-hooks/refs -- False positive
		trpc.wizard.user.deleteMany.mutationOptions({
			onSuccess: (res) => {
				if (!res.isSuccess) {
					return;
				}

				const deletedIds = new Set(res.deletedUserIds);
				queryClient.setQueryData(trpc.wizard.user.getMany.queryKey(), (data) => {
					if (!data) {
						return data;
					}
					const users = data.users.filter((user) => !deletedIds.has(user.id));
					return { ...data, users, totalCount: users.length };
				});

				setServerUsers((prev) => prev.filter((user) => !deletedIds.has(user.id)));
				deletedUsersSanityCheck.current.clear();
			},
		}),
	);

	const { mutateAsync: assignCapabilitiesToManyUsers, isPending: assignCapabilitiesIsPending } = useMutation(
		trpc.wizard.user.capabilities.assignToManyUsers.mutationOptions({
			onSuccess: (res) => {
				const capabilitiesByUserId = new Map(res.data.map((u) => [u.userId, u.capabilities]));

				queryClient.setQueryData(trpc.wizard.user.getMany.queryKey(), (data) =>
					data ?
						{
							...data,
							users: data.users.map((user) => {
								const capabilities = capabilitiesByUserId.get(user.id);
								return capabilities ? { ...user, capabilities } : user;
							}),
						}
					:	data,
				);

				setServerUsers((prev) =>
					prev.map((user) => {
						const capabilities = capabilitiesByUserId.get(user.id);
						return capabilities ? { ...user, capabilities } : user;
					}),
				);
			},
		}),
	);

	const {
		draftState: draftUsers,
		setDraftState: setDraftUsers,
		setServerState: setServerUsers,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: users,
		mutationKeys: [
			trpc.wizard.user.capabilities.assignToManyUsers.mutationKey(),
			trpc.wizard.user.deleteMany.mutationKey(),
		],
		saveFn: async (draftUsers, serverUsers) => {
			await assignCapabilitiesToManyUsers({
				payload: draftUsers.map((user) => ({
					userId: user.id,
					capabilities: user.capabilities,
				})),
			});

			// A user is removed if it was in the server users but not in the draft users
			const userIdsToDelete = serverUsers
				.filter((user) => !draftUsers.find((u) => u.id === user.id))
				.map((user) => user.id);
			for (const userId of userIdsToDelete) {
				if (!deletedUsersSanityCheck.current.has(userId)) {
					throw new Error(`Tried to delete user ${userId} but it was not in the deleted users set`);
				}
			}
			await deleteManyUsers({
				userIds: userIdsToDelete,
			});
		},
	});

	const handleRemoveUser = (userId: string) => {
		setDraftUsers((prev) => prev.filter((user) => user.id !== userId));
		deletedUsersSanityCheck.current.add(userId);
	};

	const handleChangeUserCapability = (userId: string, capability: string, enabled: boolean) => {
		setDraftUsers((prev) =>
			prev.map((user) => {
				if (user.id === userId) {
					return {
						...user,
						capabilities: {
							...user.capabilities,
							[capability]: enabled,
						},
					};
				}

				return user;
			}),
		);
	};

	const el = useDataElTagGenerator(WizardAdminUsersPage);
	return (
		<>
			<div {...el()} ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 flex-col gap-1.5 p-2'>
				<div className='flex items-end justify-between gap-4 border-b border-gray-a4'>
					<Tabs
						tabs={tabDefs}
						selectedTab={selectedTab}
						onSelectedTabChange={handleSelectedTabChange}
						renderTab={(props) => <UnderlineTab {...props} />}
					/>

					{selectedTab === WizardAdminUsersTab.Access && (
						<div className='flex items-center gap-2 pb-1'>
							<Button
								startIcon={<AddIcon />}
								onClick={(e) => setShowInviteForm({ anchorEl: e.currentTarget, nonce: nanoid() })}
							>
								<span>Invite user</span>
							</Button>
							<Button
								disabled={!isDirty}
								loading={assignCapabilitiesIsPending || deleteManyUsersIsPending}
								startIcon={<SaveIcon />}
								onClick={() => void handleSave()}
							>
								<span>Save</span>
							</Button>
						</div>
					)}
				</div>

				{selectedTab === WizardAdminUsersTab.Access ?
					<WizardUserTable
						users={draftUsers}
						onRemoveUser={handleRemoveUser}
						onChangeCapability={handleChangeUserCapability}
					/>
				:	<ReviewerTiersTab />}
			</div>

			<Popover
				anchorEl={showInviteForm === false ? undefined : showInviteForm.anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={showInviteForm !== false}
				onClose={() => setShowInviteForm(false)}
			>
				{showInviteForm !== false && (
					<InviteWizardUserForm key={showInviteForm.nonce} onClose={() => setShowInviteForm(false)} />
				)}
			</Popover>
		</>
	);
}
